import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { groupService } from '../../services/group.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { userService } from '../../services/user.service'
import { Group, User } from '../../types'
import './PermissionsDialog.scss'

export interface PermissionsFormData {
  owner: number | null
  set_permissions: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

interface PermissionsDialogProps {
  show: boolean
  onHide: () => void
  onConfirm: (permissions: PermissionsFormData, merge: boolean) => void | Promise<void>
  object?: any // If editing single object
  selectedCount?: number // If bulk editing
}

export const PermissionsDialog: React.FC<PermissionsDialogProps> = ({ show, onHide, onConfirm, object, selectedCount }) => {
  const { t } = useTranslation()
  const [users, setUsers] = useState<User[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(false)

  const selectMenuStyles = {
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 1060,
    }),
  }

  const [formData, setFormData] = useState<PermissionsFormData>({
    owner: null,
    set_permissions: {
      view: {
        users: [],
        groups: [],
      },
      change: {
        users: [],
        groups: [],
      },
    },
  })

  const [merge, setMerge] = useState(true)

  useEffect(() => {
    if (show) {
      loadUsersAndGroups().catch(() => {})

      // If editing single object, load its permissions
      if (object && object.permissions) {
        setFormData({
          owner: object.owner || null,
          set_permissions: object.permissions,
        })
      } else {
        // Reset for bulk edit
        setFormData({
          owner: null,
          set_permissions: {
            view: {
              users: [],
              groups: [],
            },
            change: {
              users: [],
              groups: [],
            },
          },
        })
      }
    } else {
      setLoading(false)
    }
  }, [show, object])

  const loadUsersAndGroups = async () => {
    try {
      const [usersResponse, groupsResponse] = await Promise.all([
        permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)
          ? userService.listAll({ page: 1, page_size: 100000, full_perms: true })
          : Promise.resolve({ results: [] as User[] }),
        permissionsService.currentUserCan(PermissionAction.View, PermissionType.Group)
          ? groupService.listAll({ page: 1, page_size: 100000, full_perms: true })
          : Promise.resolve({ results: [] as Group[] }),
      ])
      setUsers(usersResponse.results)
      setGroups(groupsResponse.results)
    } catch (error) {}
  }

  const handleOwnerChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      owner: value && value !== '0' ? parseInt(value) : null,
    }))
  }

  const handlePermissionChange = (type: 'view' | 'change', field: 'users' | 'groups', values: number[]) => {
    setFormData((prev) => ({
      ...prev,
      set_permissions: {
        ...prev.set_permissions,
        [type]: {
          ...prev.set_permissions[type],
          [field]: values,
        },
      },
    }))
  }

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm(formData, merge)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    return t('set_permissions') || 'Postavi dozvole'
  }

  const getHint = () => {
    if (!object && selectedCount) {
      return t('permissions_bulk_edit_hint')
    }
    return null
  }

  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={!loading} size="lg" className="permissions-dialog">
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Owner */}
          <Form.Group className="mb-3">
            <Form.Label>{t('owner')}:</Form.Label>
            <div className="docdot-input-select">
              <Select
                value={
                  formData.owner
                    ? {
                        value: formData.owner,
                        label: users.find((u) => u.id === formData.owner)?.username || '',
                      }
                    : {
                        value: 0,
                        label: t('no_owner'),
                      }
                }
                onChange={(selectedOption) => {
                  handleOwnerChange(selectedOption ? selectedOption.value.toString() : '')
                }}
                options={[
                  { value: 0, label: t('no_owner') },
                  ...users.map((user) => ({
                    value: user.id,
                    label: user.username,
                  })),
                ]}
                placeholder={t('select_owner')}
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                isDisabled={loading}
                styles={selectMenuStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
              />
            </div>
            <Form.Text className="text-muted">{t('objects_without_owner_can_be_viewed_by_all')}</Form.Text>
          </Form.Group>

          {/* View Permissions */}
          <div className="mb-4">
            <h6>{t('view')}:</h6>

            <Form.Group className="mb-2">
              <Form.Label>{t('users')}:</Form.Label>
              <div className="docdot-input-select">
                <Select
                  isMulti
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.username,
                  }))}
                  value={users
                    .filter((user) => formData.set_permissions.view.users.includes(user.id))
                    .map((user) => ({
                      value: user.id,
                      label: user.username,
                    }))}
                  onChange={(selectedOptions) => {
                    const selectedUsers = selectedOptions ? selectedOptions.map((option) => option.value) : []
                    handlePermissionChange('view', 'users', selectedUsers)
                  }}
                  placeholder={t('select_users')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={false}
                  closeMenuOnSelect={false}
                  isDisabled={loading}
                  styles={selectMenuStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>{t('groups')}:</Form.Label>
              <div className="docdot-input-select">
                <Select
                  isMulti
                  options={groups.map((group) => ({
                    value: group.id,
                    label: group.name,
                  }))}
                  value={groups
                    .filter((group) => formData.set_permissions.view.groups.includes(group.id))
                    .map((group) => ({
                      value: group.id,
                      label: group.name,
                    }))}
                  onChange={(selectedOptions) => {
                    const selectedGroups = selectedOptions ? selectedOptions.map((option) => option.value) : []
                    handlePermissionChange('view', 'groups', selectedGroups)
                  }}
                  placeholder={t('select_groups')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={false}
                  closeMenuOnSelect={false}
                  isDisabled={loading}
                  styles={selectMenuStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </Form.Group>
          </div>

          {/* Change Permissions */}
          <div className="mb-4">
            <h6>{t('edit')}:</h6>

            <Form.Group className="mb-2">
              <Form.Label>{t('users')}:</Form.Label>
              <div className="docdot-input-select">
                <Select
                  isMulti
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.username,
                  }))}
                  value={users
                    .filter((user) => formData.set_permissions.change.users.includes(user.id))
                    .map((user) => ({
                      value: user.id,
                      label: user.username,
                    }))}
                  onChange={(selectedOptions) => {
                    const selectedUsers = selectedOptions ? selectedOptions.map((option) => option.value) : []
                    handlePermissionChange('change', 'users', selectedUsers)
                  }}
                  placeholder={t('select_users')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={false}
                  closeMenuOnSelect={false}
                  isDisabled={loading}
                  styles={selectMenuStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>{t('groups')}:</Form.Label>
              <div className="docdot-input-select">
                <Select
                  isMulti
                  options={groups.map((group) => ({
                    value: group.id,
                    label: group.name,
                  }))}
                  value={groups
                    .filter((group) => formData.set_permissions.change.groups.includes(group.id))
                    .map((group) => ({
                      value: group.id,
                      label: group.name,
                    }))}
                  onChange={(selectedOptions) => {
                    const selectedGroups = selectedOptions ? selectedOptions.map((option) => option.value) : []
                    handlePermissionChange('change', 'groups', selectedGroups)
                  }}
                  placeholder={t('select_groups')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={false}
                  closeMenuOnSelect={false}
                  isDisabled={loading}
                  styles={selectMenuStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </Form.Group>
            <Form.Text className="text-muted">{t('edit_permissions_also_grant_view_permissions')}</Form.Text>
          </div>

          {/* Merge checkbox for bulk edit */}
          {!object && selectedCount && (
            <Form.Group className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="merge-permissions"
                  checked={merge}
                  onChange={(e) => setMerge(e.target.checked)}
                  label={t('merge_with_existing_permissions')}
                  className="me-3"
                  disabled={loading}
                />
              </div>
              {getHint() && <Form.Text className="text-muted d-block mt-2">{getHint()}</Form.Text>}
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {loading && (
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        )}
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={loading}>
          {t('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
